/**
 * useExportAlerts Hook
 *
 * Manages export functionality for alerts and products.
 * Handles modal state, format selection, and file download.
 *
 * Uses the export service layer for API operations.
 * @see ../../services/export for API implementation
 */

import { useState, useCallback } from 'react';
import { useToastHelpers } from '../../components/feedback/ToastProvider';
import { useTimeout } from '../core/useTimeout';
import * as exportApi from '../../services/export';
import { ExportApiError, type ExportFormat } from '../../services/export';
import { DEFAULT_EXPORT_FIELDS, EXPORT_FIELD_OPTIONS } from '../../services/export';

// Re-export for consumers
export { type ExportFormat, EXPORT_FIELD_OPTIONS, DEFAULT_EXPORT_FIELDS };

/**
 * Export state
 */
export interface ExportState {
  modalOpen: boolean;
  format: ExportFormat;
  fields: string[];
  isExporting: boolean;
}

/**
 * Export counts for success message
 */
export interface ExportCounts {
  alertsCount: number;
  productsCount: number;
}

/**
 * Return type for useExportAlerts hook
 */
export interface UseExportAlertsReturn {
  // State
  exportState: ExportState;

  // Modal controls
  openExportModal: () => void;
  closeExportModal: () => void;

  // Format and field setters
  setExportFormat: (format: string) => void;
  setExportFields: (fields: string[]) => void;

  // Export handler
  handleExport: (selectedTab: number, counts: ExportCounts) => Promise<void>;
}

/**
 * Hook for managing export functionality
 *
 * @returns Export state and handlers
 */
export function useExportAlerts(): UseExportAlertsReturn {
  const toast = useToastHelpers();

  // Export modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [fields, setFields] = useState<string[]>(DEFAULT_EXPORT_FIELDS);
  const [isExporting, setIsExporting] = useState(false);

  // Timeout handling for export operations
  const exportTimeout = useTimeout({
    onWarning: () => {
      toast.info('Export is taking longer than expected...');
    },
    onExtended: () => {
      toast.info('Still working on the export...');
    },
    onTimeout: () => {
      toast.error('Export operation timed out. Please try exporting a smaller dataset.');
    },
  });

  /**
   * Open export modal
   */
  const openExportModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  /**
   * Close export modal
   */
  const closeExportModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  /**
   * Set export format
   */
  const setExportFormat = useCallback((newFormat: string) => {
    setFormat(newFormat as ExportFormat);
  }, []);

  /**
   * Set export fields
   */
  const setExportFields = useCallback((newFields: string[]) => {
    setFields(newFields);
  }, []);

  /**
   * Handle export action
   */
  const handleExport = useCallback(async (selectedTab: number, counts: ExportCounts) => {
    setIsExporting(true);
    exportTimeout.start();

    try {
      // Determine which export type based on selected tab
      const exportType = selectedTab === 1 ? 'products' : 'alerts';

      // Use service for export and download
      await exportApi.exportAndDownload({
        type: exportType,
        format,
        fields: exportType === 'alerts' ? fields : undefined,
        tab: exportType === 'alerts' ? selectedTab : undefined,
      });

      const exportCount = exportType === 'products' ? counts.productsCount : counts.alertsCount;
      const exportLabel = exportType === 'products' ? 'products' : 'alerts';
      toast.success(`Exported ${exportCount} ${exportLabel} as ${format.toUpperCase()}`);
      setModalOpen(false);
    } catch (error) {
      // Export failed - show toast error
      const errorLabel = selectedTab === 1 ? 'products' : 'alerts';
      toast.error(error instanceof ExportApiError ? error.message : `Failed to export ${errorLabel}`);
    } finally {
      exportTimeout.stop();
      setIsExporting(false);
    }
  }, [format, fields, toast, exportTimeout]);

  return {
    // State
    exportState: {
      modalOpen,
      format,
      fields,
      isExporting,
    },

    // Modal controls
    openExportModal,
    closeExportModal,

    // Format and field setters
    setExportFormat,
    setExportFields,

    // Export handler
    handleExport,
  };
}
